import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, createSlots as _createSlots, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, Fragment as _Fragment, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "table-wrapper" }
const _hoisted_2 = ["id", "onMouseover", "onMouseleave", "onClick"]
const _hoisted_3 = { key: 0 }

import TTable from "vue-tailwind/dist/t-table";
import { VInputCheckbox } from "@/components";
import VPagination from "@/components/VPagination/VPagination.vue";
import EmptyMessage from "./EmptyMessage";
import THead from "./THead";
import VInputSelect from "@/components/VInputSelect/VInputSelect.vue";
import { EventBus } from "@/plugins/event-bus";
import {
  ref,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
  useSlots,
} from "vue";
import store from "@/store";
import { TranslateResult } from "vue-i18n";
import { isExistSlot } from "@/plugins/helpers";

interface Props {
  columns: Array<{
    key: string;
    title?: string | TranslateResult;
    thClass?: string;
    tdClass?: string;
    sortable?: boolean;
    field?: string;
  }>;
  rows: Array<string>;
  rowIdBy?: string;
  hiddenColumns?: Array<boolean>;
  sort?: {
    column?: string;
    direction?: string;
  };
  selectable?: boolean;
  paginationData?: {
    page?: number;
    per_page?: number;
    total_pages?: number;
    current_page?: number;
  };
  hidePerPage?: boolean;
  hidePagination?: boolean;
  customScrollTop?: number;
  rowHoverIndex?: number | null;
  shadow?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VTable',
  props: {
    columns: { default: () => [
    {
      key: "",
      title: "",
      thClass: "",
      tdClass: "",
      sortable: true,
      field: "",
    },
  ] },
    rows: { default: () => [] },
    rowIdBy: { default: "" },
    hiddenColumns: { default: () => [] },
    sort: { default: () => {
    return { column: "", direction: "" };
  } },
    selectable: { type: Boolean, default: false },
    paginationData: { default: () => {
    return {
      page: 1,
      per_page: 1,
      total_pages: 1,
      current_page: 1,
    };
  } },
    hidePerPage: { type: Boolean, default: false },
    hidePagination: { type: Boolean, default: false },
    customScrollTop: { default: 0 },
    rowHoverIndex: { default: null },
    shadow: { type: Boolean, default: false }
  },
  emits: [
  "sort",
  "changePage",
  "changeScrollTop",
  "changePerPage",
  "clickRow",
  "updateHoverRowIndex",
],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit;
const scroll = ref();
const table = ref();
const thead = ref();

const slots = useSlots();

const props = __props;

const selectData = ref<Array<{ rowIndex: number; selected: boolean }>>([]);

const perPageOptions = computed(() => store.getters["table/perPageOptions"]);
const isFullscreenContainerPage = computed(
  () => store.getters["viewport/isFullscreenContainerPage"]
);

const tableClasses = computed(() => {
  return { "table-shadow": props.shadow };
});

const columnValues = computed(() =>
  props.columns.filter(({ key }) => !props.hiddenColumns.includes(key))
);

const selectedRowIndexes = computed(() =>
  selectData.value.filter(({ selected }) => selected).map(({ rowIndex }) => rowIndex)
);

const sortValue = computed({
  get() {
    return props.sort;
  },
  set(value) {
    emit("sort", value);
  },
});

const columnsCount = computed(() =>
  props.selectable ? columnValues.value.length + 1 : columnValues.value.length
);

const selectAll = () => {
  selectData.value.map((row) => {
    row.selected = true;
  });
};

const deselectAll = () => {
  selectData.value.map((row) => {
    row.selected = false;
  });
};

const batchActionValue = computed({
  get() {
    const selectedRows = selectData.value.filter(({ selected }) => selected);
    let value = "selectedNone";

    if (selectedRows.length) {
      value = selectedRows.length === props.rows.length ? "selectedAll" : "selectedPartly";
    }

    return value;
  },
  set(value) {
    switch (value) {
      case "selectedAll":
        selectAll();
        break;
      case "selectedNone":
        deselectAll();
        break;
    }
  },
});

const isShownPagination = computed(
  () => Object.keys(props.paginationData).length && !props.hidePagination
);

const isShownTableHeader = computed(() => !!columnValues.value.filter(({ title }) => title).length);
const showPerPageSelect = computed(() => props.paginationData.per_page && !props.hidePerPage);
const paginationContainerClasses = computed(() => {
  return {
    "without-per-page": props.hidePerPage,
  };
});

const onChangeRows = (rows: Array<{ selected: boolean }>) => {
  selectData.value = rows.map((row, rowIndex) => ({
    rowIndex,
    selected: false,
  }));
};

const updateScroll = () => {
  scroll.value.update();
};

const setCustomScrollTop = () => {
  scroll.value.ps.element.scrollTop = props.customScrollTop;

  updateScroll();
};

const setTableHeaderPosition = ({ type }) => {
  let tableTop = 0;

  if (table.value?.$el && type === "scroll") {
    tableTop = table.value.$el.getBoundingClientRect().top;

    if (!isFullscreenContainerPage.value) {
      tableTop = tableTop - 12;
    }
  }

  if (thead.value?.$el) {
    thead.value.$el.style.transform = tableTop < 0 ? `translateY(calc(${tableTop * -1}px))` : "";
  }
};

const onUpdatePaginationData = () => {
  if (props.paginationData.current_page > props.paginationData.total_pages) {
    emit("changePage", props.paginationData.total_pages);
  }
};

const getTrId = (row: any, rowIndex: number) => {
  const uniqueClass = props.rowIdBy ? `row-${row[props.rowIdBy]}` : `row-${rowIndex}`;

  return [uniqueClass];
};

const onChangeScrollTop = () => {
  const { scrollTop } = scroll.value.ps.element;

  emit("changeScrollTop", scrollTop);
};

const changePage = (page: number) => {
  emit("changePage", page);
};

const changePerPage = (perPage: number) => {
  emit("changePerPage", perPage);
};

const getTdClasses = (item: { key: string; tdClass: string }) => {
  return [item.tdClass, `${item.key}-td`];
};

const getTrClasses = (rowIndex: number) => {
  const hasBg = rowIndex % 2 === 0;

  return {
    "hover-active": rowIndex === props.rowHoverIndex,
    "bg-whisper-gray": hasBg,
  };
};

const onHoverRow = (rowIndex: number) => {
  emit("updateHoverRowIndex", rowIndex);
};

const onClickRow = (row: any) => {
  emit("clickRow", row);
};

const onLeaveRow = () => {
  emit("updateHoverRowIndex", null);
};

onBeforeUnmount(() => {
  window.removeEventListener("resize", setTableHeaderPosition);
});

watch(() => props.rows, onChangeRows, { immediate: true, deep: true });
watch(() => props.customScrollTop, setCustomScrollTop);
watch(() => props.paginationData, onUpdatePaginationData, { immediate: true, deep: true });

onMounted(() => {
  window.addEventListener("resize", setTableHeaderPosition);

  EventBus.$on("containerPageScroll", setTableHeaderPosition);
});

__expose({
  selectedRowIndexes,
});

return (_ctx: any,_cache: any) => {
  const _component_perfect_scrollbar = _resolveComponent("perfect-scrollbar")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["table-container", tableClasses.value])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_perfect_scrollbar, {
        ref_key: "scroll",
        ref: scroll,
        class: "perfect-scrollbar",
        onMousemove: updateScroll,
        onPsScrollY: onChangeScrollTop
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(TTable), {
            ref_key: "table",
            ref: table,
            headers: columnValues.value,
            data: _ctx.rows,
            classes: {}
          }, _createSlots({
            thead: _withCtx(({ data }) => [
              _createVNode(_unref(THead), {
                ref_key: "thead",
                ref: thead,
                "is-shown-table-header": isShownTableHeader.value,
                selectable: _ctx.selectable,
                data: data,
                "batch-action-value": batchActionValue.value,
                "sort-value": sortValue.value,
                onBatchActionValue: _cache[0] || (_cache[0] = ($event: any) => (batchActionValue.value = $event)),
                onSortValue: _cache[1] || (_cache[1] = ($event: any) => (sortValue.value = $event))
              }, _createSlots({ _: 2 }, [
                _renderList(data, (item) => {
                  return {
                    name: `title-${item.key}`,
                    fn: _withCtx(() => [
                      (_unref(isExistSlot)(`title-${item.key}`, _unref(slots)))
                        ? _renderSlot(_ctx.$slots, `title-${item.key}`, {
                            key: 0,
                            title: item.title
                          })
                        : _renderSlot(_ctx.$slots, `title-${item.key}`, {
                            key: 1,
                            title: item.title
                          }, () => [
                            _createTextVNode(_toDisplayString(item.title), 1)
                          ])
                    ])
                  }
                })
              ]), 1032, ["is-shown-table-header", "selectable", "data", "batch-action-value", "sort-value"])
            ]),
            row: _withCtx(({ row, rowIndex }) => [
              _createElementVNode("tr", {
                id: getTrId(row, rowIndex),
                class: _normalizeClass(getTrClasses(rowIndex)),
                onMouseover: ($event: any) => (onHoverRow(rowIndex)),
                onMouseleave: ($event: any) => (onLeaveRow(rowIndex)),
                onClick: ($event: any) => (onClickRow(row))
              }, [
                (_ctx.selectable)
                  ? (_openBlock(), _createElementBlock("td", _hoisted_3, [
                      _createVNode(_unref(VInputCheckbox), {
                        modelValue: selectData.value[rowIndex].selected,
                        "onUpdate:modelValue": ($event: any) => ((selectData.value[rowIndex].selected) = $event),
                        "data-cy": `checkbox-${rowIndex}`
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "data-cy"])
                    ]))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columnValues.value, (item, index) => {
                  return (_openBlock(), _createElementBlock("td", {
                    key: index,
                    class: _normalizeClass(getTdClasses(item))
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["cell", `${item.key}-cell`])
                    }, [
                      (_unref(isExistSlot)(`cell-${item.key}`, _unref(slots)))
                        ? _renderSlot(_ctx.$slots, `cell-${item.key}`, {
                            key: 0,
                            value: row[item.key],
                            row: row,
                            rowIndex: rowIndex
                          })
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createTextVNode(_toDisplayString(row[item.key]), 1)
                          ], 64))
                    ], 2)
                  ], 2))
                }), 128))
              ], 42, _hoisted_2)
            ]),
            _: 2
          }, [
            (!_ctx.rows.length)
              ? {
                  name: "tbody",
                  fn: _withCtx(() => [
                    _createVNode(_unref(EmptyMessage), { colspan: columnsCount.value }, null, 8, ["colspan"])
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["headers", "data"])
        ], undefined, true),
        _: 3
      }, 512)
    ]),
    _createElementVNode("div", {
      ref: "pagination",
      class: _normalizeClass(["pagination-container", paginationContainerClasses.value])
    }, [
      (showPerPageSelect.value)
        ? (_openBlock(), _createBlock(VInputSelect, {
            key: 0,
            "data-cy": "records-per-page",
            class: "per-page-select",
            "track-by": "value",
            options: perPageOptions.value,
            value: _ctx.paginationData.per_page,
            label: _ctx.$t('label.recordsPerPage'),
            "allow-empty": false,
            "label-prepend": "",
            "hide-placeholder": "",
            "open-direction": "top",
            searchable: false,
            onInput: changePerPage
          }, null, 8, ["options", "value", "label"]))
        : _createCommentVNode("", true),
      (isShownPagination.value)
        ? (_openBlock(), _createBlock(VPagination, {
            key: 1,
            value: _ctx.paginationData.current_page,
            "per-page": _ctx.paginationData.per_page,
            total: _ctx.paginationData.total,
            limit: _ctx.paginationData.limit,
            onInput: changePage
          }, null, 8, ["value", "per-page", "total", "limit"]))
        : _createCommentVNode("", true)
    ], 2)
  ], 2))
}
}

})